import React, { useState, useRef, useEffect } from 'react';
import { FaBook, FaExclamationCircle } from 'react-icons/fa';
import { Tag, X } from 'lucide-react';

const PathHeader = ({ data, onChange, errors = {}, touched = {}, readOnly = false, tags = [], availableTags = [] }) => {
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [tagSearchTerm, setTagSearchTerm] = useState('');
  const tagDropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tagDropdownRef.current && !tagDropdownRef.current.contains(event.target)) {
        setShowTagDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleTag = (tagId) => {
    const newTags = tags.includes(tagId)
      ? tags.filter(id => id !== tagId)
      : [...tags, tagId];
    onChange({ tags: newTags });
    setTagSearchTerm('');
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const removeTag = (tagId) => {
    const newTags = tags.filter(id => id !== tagId);
    onChange({ tags: newTags });
  };

  const getSelectedTagObjects = () => {
    return availableTags.filter(tag => tags.includes(tag._id));
  };

  const getFilteredTags = () => {
    return availableTags.filter(tag => 
      !tags.includes(tag._id) && 
      tag.title.toLowerCase().includes(tagSearchTerm.toLowerCase())
    );
  };

  const handleTagInputClick = () => {
    setShowTagDropdown(true);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Backspace' && tagSearchTerm === '' && tags.length > 0) {
      removeTag(tags[tags.length - 1]);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <FaBook className="w-8 h-8 text-[#1086a4]" />
        <div className="flex-1">
          {readOnly ? (
            <h1 className="text-2xl font-bold text-[#054581]">{data.title}</h1>
          ) : (
            <input
              type="text"
              value={data.title}
              onChange={(e) => onChange({ title: e.target.value })}
              placeholder="Enter path title"
              className={`w-full text-2xl font-bold border-none focus:outline-none text-[#054581] ${
                errors.title && touched.title ? 'placeholder-red-300' : ''
              }`}
              data-error={errors.title && touched.title}
            />
          )}
          {!readOnly && errors.title && touched.title && (
            <div className="flex items-center gap-2 text-red-500 text-sm mt-1">
              <FaExclamationCircle className="w-4 h-4" />
              <span>{errors.title}</span>
            </div>
          )}
        </div>
      </div>

      {/* Description Section */}
      <div className="bg-gray-50 p-3 rounded-lg border border-[#1086a4]/20">
        <h3 className="text-[#054581] font-medium mb-3 text-sm">Description</h3>
        {readOnly ? (
          data.description ? (
            <p className="text-gray-700 whitespace-pre-wrap">{data.description}</p>
          ) : (
            <p className="text-gray-400 italic">No description provided</p>
          )
        ) : (
          <textarea
            value={data.description || ''}
            onChange={(e) => onChange({ description: e.target.value })}
            placeholder="Enter path description (optional)"
            rows={4}
            className="w-full px-3 py-2 border border-[#1086a4] rounded-md focus:ring-2 focus:ring-[#1086a4] focus:border-transparent resize-vertical"
          />
        )}
      </div>

      <div className="bg-gray-50 p-3 rounded-lg border border-[#1086a4]/20">
        <h3 className="text-[#054581] font-medium mb-3 text-sm">Score Range</h3>
        <div className="flex gap-3">
          <div className="flex-1">
            <div className="bg-white p-2 rounded border border-[#1086a4] shadow-sm">
              <label className="block text-xs font-medium text-[#054581] mb-1">Min Score</label>
              {readOnly ? (
                <div className="text-xl font-bold text-[#1086a4]">{data.rangeMin}</div>
              ) : (
                <>
                  <input
                    type="number"
                    value={data.rangeMin}
                    onChange={(e) => onChange({ rangeMin: parseInt(e.target.value) })}
                    className={`w-full text-xl font-bold text-[#1086a4] border-0 focus:ring-0 p-0 ${
                      (errors.rangeMin || errors.range) && touched.rangeMin ? 'text-red-500' : ''
                    }`}
                    min="0"
                    data-error={(errors.rangeMin || errors.range) && touched.rangeMin}
                  />
                  {errors.rangeMin && touched.rangeMin && (
                    <div className="text-red-500 text-xs mt-1">
                      {errors.rangeMin}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="flex-1">
            <div className="bg-white p-2 rounded border border-[#1086a4] shadow-sm">
              <label className="block text-xs font-medium text-[#054581] mb-1">Max Score</label>
              {readOnly ? (
                <div className="text-xl font-bold text-[#1086a4]">{data.rangeMax}</div>
              ) : (
                <>
                  <input
                    type="number"
                    value={data.rangeMax}
                    onChange={(e) => onChange({ rangeMax: parseInt(e.target.value) })}
                    className={`w-full text-xl font-bold text-[#1086a4] border-0 focus:ring-0 p-0 ${
                      (errors.rangeMax || errors.range) && touched.rangeMax ? 'text-red-500' : ''
                    }`}
                    min="0"
                    data-error={(errors.rangeMax || errors.range) && touched.rangeMax}
                  />
                  {errors.rangeMax && touched.rangeMax && (
                    <div className="text-red-500 text-xs mt-1">
                      {errors.rangeMax}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {!readOnly && errors.range && (touched.rangeMin || touched.rangeMax) && (
          <div className="mt-2 text-red-500 text-xs bg-red-50 p-2 rounded">
            {errors.range}
          </div>
        )}
      </div>

      {/* Tags Section */}
      <div className="bg-gray-50 p-3 rounded-lg border border-[#1086a4]/20">
        <h3 className="text-[#054581] font-medium mb-3 text-sm flex items-center gap-2">
          <Tag className="w-4 h-4" />
          Tags
        </h3>

        {!readOnly && (
          <div className="relative" ref={tagDropdownRef}>
            <div
              onClick={handleTagInputClick}
              className="min-h-[42px] px-3 py-2 bg-[#f9fafb] border border-[#1086a4] rounded-md cursor-text focus-within:ring-2 focus-within:ring-[#1086a4] focus-within:border-transparent transition-all"
            >
              <div className="flex flex-wrap items-center gap-1">
                {getSelectedTagObjects().map(tag => (
                  <span
                    key={tag._id}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-[#1086a4] bg-opacity-20 text-[#1086a4] border border-[#1086a4] border-opacity-30"
                  >
                    <Tag className="w-3 h-3 mr-1" />
                    {tag.title}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeTag(tag._id);
                      }}
                      className="ml-1 text-[#1086a4] hover:text-[#054581] hover:bg-[#1086a4] hover:bg-opacity-20 rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                <input
                  ref={searchInputRef}
                  type="text"
                  value={tagSearchTerm}
                  onChange={(e) => setTagSearchTerm(e.target.value)}
                  onKeyDown={handleTagInputKeyDown}
                  onFocus={() => setShowTagDropdown(true)}
                  placeholder={tags.length === 0 ? "Search and select tags..." : "Search tags..."}
                  className="flex-1 min-w-[120px] bg-transparent border-none outline-none text-gray-900 placeholder-gray-500 text-sm"
                />
              </div>
            </div>
            {showTagDropdown && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-[#1086a4] rounded-md shadow-lg max-h-48 overflow-auto">
                {getFilteredTags().length > 0 ? (
                  getFilteredTags().map(tag => (
                    <button
                      key={tag._id}
                      type="button"
                      onClick={() => toggleTag(tag._id)}
                      className="w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors flex items-center"
                    >
                      <Tag className="w-3 h-3 mr-2 text-[#1086a4]" />
                      {tag.title}
                    </button>
                  ))
                ) : (
                  <div className="px-3 py-2 text-gray-500 text-sm">
                    {tagSearchTerm ? `No tags found for "${tagSearchTerm}"` : 'No more tags available'}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {readOnly && getSelectedTagObjects().length > 0 && (
          <div className="flex flex-wrap gap-2">
            {getSelectedTagObjects().map(tag => (
              <span
                key={tag._id}
                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-[#1086a4] bg-opacity-10 text-[#1086a4]"
              >
                <Tag className="w-3 h-3 mr-1" />
                {tag.title}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PathHeader; 